import { useEffect, useState } from "react";
import type { CampaignOperationDetail, CampaignOperationLocale } from "@atsready/shared";
import { Button } from "@/components/ui/button";
import {
  campaignEmailButtonClass,
  campaignEmailCopy,
  campaignEmailReason,
} from "@/lib/campaign-email-copy";

export function CampaignEmailProgress({
  detail,
  locale,
  busy,
  running,
  blockedUntil,
  onResume,
  onPause,
  onRetry,
  onReconcile,
  onReload,
  onNext,
  onPrevious,
  hasPrevious,
}: {
  detail: CampaignOperationDetail;
  locale: CampaignOperationLocale;
  busy: boolean;
  running: boolean;
  blockedUntil: number;
  onResume: () => void;
  onPause: () => void;
  onRetry: () => void;
  onReconcile: (id: string) => void;
  onReload: () => void;
  onNext: () => void;
  onPrevious: () => void;
  hasPrevious: boolean;
}) {
  const copy = campaignEmailCopy[locale];
  const { operation, recipients } = detail;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const deadlines = [
      operation.providerBlockedUntil,
      ...recipients.flatMap((recipient) => [
        recipient.preparedSendBy,
        recipient.firstAttemptAt &&
          new Date(Date.parse(recipient.firstAttemptAt) + 23 * 3600_000).toISOString(),
      ]),
    ]
      .filter((date): date is string => date !== null)
      .map(Date.parse);
    let timer: ReturnType<typeof setTimeout>;
    const updateClock = () => {
      const current = Date.now();
      setNow(current);
      const future = deadlines.filter((time) => time > current);
      if (future.length)
        timer = setTimeout(updateClock, Math.min(2_147_483_647, Math.min(...future) - current));
    };
    timer = setTimeout(updateClock, 0);
    return () => clearTimeout(timer);
  }, [operation.providerBlockedUntil, recipients]);
  const providerCooling =
    !!operation.providerBlockedUntil && Date.parse(operation.providerBlockedUntil) > now;
  const blocked =
    busy ||
    blockedUntil > now ||
    providerCooling ||
    (!operation.providerBlockedUntil && !!operation.providerBlockReason);
  const confirmed = operation.reviewState === "confirmed";
  return (
    <section className="min-w-0 space-y-3" aria-busy={busy}>
      <h3 className="font-semibold">{copy.progress}</h3>
      <p className="break-words">
        {operation.campaignName} · {operation.id}
      </p>
      <p>
        {copy[operation.kind]}
        {operation.durationDays !== null ? ` · ${operation.durationDays} ${copy.duration}` : ""}
      </p>
      <p className="break-words">
        {copy.reason}: {operation.reason}
      </p>
      <p role="status" aria-live="polite" aria-atomic="true">
        {copy.granted}: {operation.counts.granted} · {copy.accepted}: {operation.counts.accepted} ·{" "}
        {copy.pending}: {operation.counts.pending} · {copy.excluded}: {operation.counts.excluded} ·{" "}
        {copy.unknown}: {operation.counts.unknown}
      </p>
      <p className="text-sm text-muted-foreground">{copy.acceptedHelp}</p>
      <p className="text-sm text-muted-foreground">{copy.pendingHelp}</p>
      {(providerCooling || (!operation.providerBlockedUntil && operation.providerBlockReason)) && (
        <p>
          {copy.providerPause}
          {operation.providerBlockedUntil
            ? ` ${copy.blockedUntil}: ${operation.providerBlockedUntil}`
            : ""}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {running ? (
          <Button className={campaignEmailButtonClass} variant="outline" onClick={onPause}>
            {copy.pause}
          </Button>
        ) : (
          <Button
            className={campaignEmailButtonClass}
            disabled={
              blocked ||
              !confirmed ||
              operation.counts.pending + operation.counts.preparing + operation.counts.sending ===
                0 ||
              operation.counts.unknown > 0
            }
            onClick={onResume}
          >
            {copy.resume}
          </Button>
        )}
        {operation.counts.rejected + operation.counts.preparation_failed > 0 && (
          <Button
            className={campaignEmailButtonClass}
            disabled={blocked || !confirmed || operation.counts.unknown > 0}
            variant="outline"
            onClick={onRetry}
          >
            {copy.retry}
          </Button>
        )}
        <Button
          className={campaignEmailButtonClass}
          disabled={busy || blockedUntil > now}
          variant="outline"
          onClick={onReload}
        >
          {copy.reload}
        </Button>
      </div>
      <ul className="space-y-3">
        {recipients.map((recipient) => {
          const canReconcile =
            recipient.firstAttemptAt !== null &&
            now < Date.parse(recipient.firstAttemptAt) + 23 * 3600_000 &&
            recipient.preparedSendBy !== null &&
            now < Date.parse(recipient.preparedSendBy);
          return (
            <li key={recipient.id} className="min-w-0 space-y-1 rounded-xl border p-3">
              <p className="break-words">
                {recipient.name ? `${recipient.name} · ` : ""}
                {recipient.email} · {recipient.recipientLocale.toUpperCase()}
              </p>
              <p>
                {copy.grant}: {copy[recipient.grantStatus]} · {copy.notification}:{" "}
                {copy[recipient.notificationStatus]}
              </p>
              {recipient.grantStartAt && (
                <p>
                  {copy.start}: {recipient.grantStartAt}
                </p>
              )}
              {recipient.grantEndAt && (
                <p>
                  {copy.end}: {recipient.grantEndAt}
                </p>
              )}
              {recipient.eligibilityReason && (
                <p>{campaignEmailReason(recipient.eligibilityReason, locale)}</p>
              )}
              {recipient.notificationStatus === "unknown" &&
                (canReconcile ? (
                  <Button
                    className={campaignEmailButtonClass}
                    disabled={blocked || !confirmed}
                    variant="outline"
                    onClick={() => onReconcile(recipient.id)}
                  >
                    {copy.reconcile}
                  </Button>
                ) : (
                  <p>{copy.manual}</p>
                ))}
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap gap-2">
        {hasPrevious && (
          <Button
            className={campaignEmailButtonClass}
            disabled={busy || blockedUntil > now}
            variant="outline"
            onClick={onPrevious}
          >
            {copy.previous}
          </Button>
        )}
        {detail.nextCursor && (
          <Button
            className={campaignEmailButtonClass}
            disabled={busy || blockedUntil > now}
            variant="outline"
            onClick={onNext}
          >
            {copy.moreRecipients}
          </Button>
        )}
      </div>
    </section>
  );
}
